// Surfaces an active GitHub rate limit for the selected target: shown above the
// candidates panel whenever App holds a non-null `rateLimit` (see refreshCandidates).
import { useEffect, useState } from 'react';
import type { RateLimitInfo } from './api';

const RELATIVE_TICK_MS = 30_000;

/** Ticks at least every `intervalMs`, so a relative-time readout stays current. */
function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

function clockTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function resetPhrase(resetAtMs: number, now: number): string {
  const remainingMs = resetAtMs - now;
  if (remainingMs <= 60_000) return 'any moment now';
  return `in ${Math.round(remainingMs / 60_000)} min`;
}

export function RateLimitBanner({
  targetLabel,
  rateLimit,
  staleAsOf,
}: {
  targetLabel: string;
  rateLimit: RateLimitInfo;
  staleAsOf: string | null;
}) {
  const now = useNow(RELATIVE_TICK_MS);
  const resetAtMs = Date.parse(rateLimit.resetAt);
  const kindLabel = rateLimit.kind === 'primary' ? 'Primary' : 'Secondary';

  return (
    <div className="ratelimit-banner" role="status" aria-live="polite" data-testid="ratelimit-banner">
      <span className="ratelimit-banner-mark" aria-hidden>!</span>
      <div className="ratelimit-banner-body">
        <p className="ratelimit-banner-head">GitHub rate limit reached for {targetLabel}</p>
        <p>{kindLabel} limit. Resets at {clockTime(rateLimit.resetAt)} ({resetPhrase(resetAtMs, now)}).</p>
        {staleAsOf ? (
          <p>Showing cached data from {clockTime(staleAsOf)}. Promotion stays blocked until the reset.</p>
        ) : (
          <p>Candidates can't refresh until then.</p>
        )}
      </div>
    </div>
  );
}
